import { EMPTY_REFUND_OPERATION_CRITERIA } from "domain/entity/Refund/RefundOperationCriteria";
import { capitalize } from "lodash";
import moment from "moment";
import { RefundConstant, RefundPermissionCodePolicy } from "presentation/constant/Refund/RefundConstant";
import { useRefundVM } from "presentation/hook/Refund/useRefundVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useRefundTracked } from "presentation/store/Refund/RefundProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";


const RefundOperationPanel = () => {
    const SCREEN_CONSTANT = RefundConstant;
    const [refundState] = useRefundTracked();
    const refundVM = useRefundVM();
    const { reasonCode, searchCriteria, selectedRows, bankerIdAndBankerAcNo, operation } = refundState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();

    const saveButtonClicked = useCallback(async (operation: string) => {
        let remark = null;
        if (operation === RefundPermissionCodePolicy.REFUND_BUTTON_REJECT) {
            if (!reasonCode) {
                messageBarVM.showError("Please select the reason code first.");
                return;
            } else {
                const formattedDate: string = moment().format('DD-MM-YYYY');
                remark = `Rejected on ${formattedDate} : ${reasonCode}`;
            }
        }
        if (operation === RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS && !bankerIdAndBankerAcNo) {
            messageBarVM.showError("Please select the bank code first.");
            return;
        }
        let criteria = { ...EMPTY_REFUND_OPERATION_CRITERIA };
        criteria = {
            ...criteria,
            operation: operation,
            selectRows: selectedRows,
            remark: remark,
            bankerIdAndBankerAcNo: bankerIdAndBankerAcNo
        }
        setIsLoading(true);
        try {
            const result = await refundVM.onOperation(criteria);
            if (!result) {
                await refundVM.onSearch(searchCriteria);
                setIsLoading(false);
                messageBarVM.showSuccess(capitalize(operation) + " Successfully");
            } else {
                messageBarVM.showError(result);
                setIsLoading(false);
            }
        } catch (e) {
            setIsLoading(false);
        }
    }, [bankerIdAndBankerAcNo, messageBarVM, reasonCode, refundVM, searchCriteria, selectedRows]);

    const closeButtonClicked = async () => {
        refundVM.onCloseOperationPanel();
    }



    const memoBankerIdAndBankerAcNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={bankerIdAndBankerAcNo ?? ''}
                fieldValue={bankerIdAndBankerAcNo}
                fieldLabel={SCREEN_CONSTANT.BANK_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'bankerIdAndBankerAcNo'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={refundState.dynamicOptions.bankCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onOperationFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [bankerIdAndBankerAcNo, SCREEN_CONSTANT.BANK_CODE, refundState.dynamicOptions.bankCodeDropdownOptions, refundVM])


    const memoReasonCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={reasonCode ?? ''}
                fieldValue={reasonCode}
                fieldLabel={SCREEN_CONSTANT.REJECT_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'reasonCode'}
                maxLength={60}
                sort={false}
                isLimitOptions={true}
                options={refundState.dynamicOptions.reasonCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    refundVM.onOperationFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [reasonCode, SCREEN_CONSTANT.REJECT_CODE, refundState.dynamicOptions.reasonCodeDropdownOptions, refundVM])


    return (
        <div className='side-form-content-wrapper'>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle>
                            {operation}
                        </SidebarTitle>
                        <SidebarActionCross>
                            <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={closeButtonClicked} />
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>
            {isLoading &&
                <Loader Indicator="Spinner" size="Medium" />}

            <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {(operation === RefundPermissionCodePolicy.REFUND_BUTTON_REJECT) && memoReasonCode}
                            {(operation === RefundPermissionCodePolicy.REFUND_BUTTON_PROCESS) && memoBankerIdAndBankerAcNo}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Cancel'} size={'Small'} theme={'Secondary'} onClick={closeButtonClicked} />
                <HPHButton label={'Confirm'} size={'Small'} theme={'Primary'} onClick={() => saveButtonClicked(operation)} />
            </SidebarActionBar>

        </div>
    );
}

export default memo(RefundOperationPanel);